import React from 'react';
import PropTypes from 'prop-types';
import VstComponent from './VstComponent';

import info from '../images/i_icon.svg';
import success from '../images/success_icon.svg';
import error from '../images/error.svg';
import x from '../images/x.svg';

import VstButton from './VstButton';
import VstInput from './VstInput';

class VstToast extends VstComponent {
  constructor(props) {
    super(props);
    this.state = {
      checked: false
    };

    this.toastRef = React.createRef();

    this.setReadingTime = this.setReadingTime.bind(this);
    this.estimateReadingTime = this.estimateReadingTime.bind(this);
    this.dismiss = this.dismiss.bind(this);
    this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
    this.customBorder = this.customBorder.bind(this);
    this.customTextColor = this.customTextColor.bind(this);
    this.customBackground = this.customBackground.bind(this);
    this.customClosePosition = this.customClosePosition.bind(this);
  }

  get entering() {
    const { transitionState } = this.props;
    return transitionState === 'entering' || transitionState === 'entered';
  }

  get exiting() {
    const { transitionState } = this.props;
    return transitionState === 'exiting' || transitionState === 'exited';
  }

  componentDidMount() {
    if (!this.props.dismissible) {
      this.setReadingTime();
    }
  }

  setReadingTime() {
    const { title = '', description = '', delay } = this.props;
    const titleReadingTime = this.estimateReadingTime(title);
    const descReadingTime = this.estimateReadingTime(description);
    const totalEstimated = titleReadingTime + descReadingTime;
    const readingTime = totalEstimated > delay ? totalEstimated : delay;
    setTimeout(this.dismiss, readingTime);
  }

  estimateReadingTime(sentence) {
    if (sentence) {
      const { wordsPerMinute } = this.props;
      const totalWords = sentence.trim().split(/\s+/).length;
      const readingTime = (totalWords * 60) / wordsPerMinute;
      const estimatedTime = Math.round(readingTime) * 1000;
      return estimatedTime;
    }
    return this.props.delay;
  }

  dismiss() {
    if (this.props.onDismiss) {
      this.props.onDismiss();
    }
  }

  handleCheckboxChange() {
    this.setState(
      state => ({ checked: !state.checked }),
      () => {
        if (this.props.onCheckboxChange) {
          this.props.onCheckboxChange(this.state.checked);
        }
      }
    );
  }

  customBorder() {
    const { borderColor, borderPosition } = this.props;
    if (borderColor) {
      const borders = {
        left: 'borderLeft',
        right: 'borderRight',
        top: 'borderTop',
        bottom: 'borderBottom'
      };
      const border = borders[borderPosition];
      return {
        [border]: `5px solid ${borderColor}`
      };
    }
    return {};
  }

  customTextColor() {
    const color = this.props.titleColor;
    if (color) {
      return { color };
    }
    return {};
  }

  customBackground() {
    const { backgroundColor } = this.props;
    if (backgroundColor) {
      return { backgroundColor };
    }
    return {};
  }

  customClosePosition() {
    const verticalAlign = this.props.closeButtonPosition;
    if (verticalAlign) {
      return { verticalAlign };
    }
    return {};
  }

  renderIcon() {
    const icons = { info, error, success };
    const {
      backgroundColor, imageUrl, imgBtnFn, imgCoverStyle, type
    } = this.props;
    let toastIcon;
    let verticallyCenteredIcon;
    if (imageUrl) {
      toastIcon = (
        <div className={`vst-toast-cover-img ${imgCoverStyle}`}>
          <img alt="" src={imageUrl} />
        </div>
      );
    } else if (type !== 'none') {
      toastIcon = icons[type]();
    } else if (imgBtnFn) {
      verticallyCenteredIcon = 'centered-icon';
      toastIcon = (
        <div aria-hidden="true" className="vst-toast-button">
          <button aria-hidden="true" id="toast-btn" onClick={imgBtnFn}>
            {toastIcon}
          </button>
        </div>
      );
    }
    return (
      <div
        className={`vst-toast-type vst-toast-type-${this.props.type} ${verticallyCenteredIcon}`}
        style={backgroundColor && { backgroundColor }}
      >
        {toastIcon}
      </div>
    );
  }

  renderDescription() {
    const style = {
      maxWidth: `${this.props.maxWidth}px`
    };
    if (this.props.description || this.props.children) {
      return (
        <div className="vst-toast-description" style={style}>
          {this.props.description || this.props.children}
        </div>
      );
    }
    return null;
  }

  renderTitle() {
    const style = {
      maxWidth: `${this.props.maxWidth}px`,
      color: this.props.titleColor || ''
    };
    return (
      <div
        className={`vst-toast-title vst-toast-title-${this.props.type}`}
        style={style}
      >
        {this.props.title}
      </div>
    );
  }

  renderCheckbox() {
    if (this.props.checkboxText) {
      return (
        <div className="vst-toast-checkbox">
          <VstInput
            id={Math.random().toString()}
            type="checkbox"
            name="checkbox"
            label={this.props.checkboxText}
            checked={this.state.checked}
            onChange={() => this.handleCheckboxChange()}
          />
        </div>
      );
    }
    return null;
  }

  renderCustomRightButton() {
    return (
      <div className="vst-toast-button" style={this.customClosePosition()}>
        <VstButton
          onClick={this.dismiss}
          aria-label={this.props.rightButtonText || 'close'}
        >
          {this.props.rightButtonText}
        </VstButton>
      </div>
    );
  }

  renderRightButton() {
    if (this.props.onDismiss && this.props.rightButtonText) {
      return this.renderCustomRightButton();
    }
    if (this.props.dismissible) {
      return (
        <div className="vst-toast-button" style={this.customClosePosition()}>
          <VstButton
            onClick={this.dismiss}
            aria-label={this.props.rightButtonText || 'close'}
          >
            {this.props.rightButtonText || x()}
          </VstButton>
        </div>
      );
    }
    return null;
  }

  render() {
    let verticalPosition = {};
    if (this.exiting && this.toastRef.current) {
      verticalPosition = {
        marginTop: `-${this.toastRef.current.offsetHeight}px`
      };
    }
    return (
      <div
        className={`vst-toast ${this.props.horizontalPosition || ''} ${this
          .props.multipleToast || ''} ${
          this.props.toastOpen
            ? `slide-${this.props.verticalPosition}`
            : 'slide'
          }  ${this.exiting ? 'exiting' : ''} ${this.entering ? 'entering' : ''}`}
        style={verticalPosition}
        id={this.props.id || 'vst-toast-ref'}
        ref={this.toastRef}
      >
        <div className="vst-toast-container" style={this.customBorder()}>
          {this.renderIcon()}
          <div className="vst-toast-content">
            {this.renderTitle()}
            {this.renderDescription()}
            {this.renderCheckbox()}
          </div>
          {this.renderRightButton()}
        </div>
      </div>
    );
  }
}

export default VstToast;

VstToast.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node,
  description: PropTypes.string,
  delay: PropTypes.number,
  fadeIn: PropTypes.number,
  fadeOut: PropTypes.number,
  maxWidth: PropTypes.number,
  wordsPerMinute: PropTypes.number,
  onDismiss: PropTypes.func,
  onCheckboxChange: PropTypes.func,
  rightButtonText: PropTypes.string,
  checkboxText: PropTypes.string,
  type: PropTypes.oneOf(['info', 'success', 'error', 'none']),
  titleColor: PropTypes.string,
  backgroundColor: PropTypes.string,
  borderColor: PropTypes.string,
  imageUrl: PropTypes.string,
  imgBtnFn: PropTypes.func,
  imgCoverStyle: PropTypes.string,
  borderPosition: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  closeButtonPosition: PropTypes.oneOf(['top', 'middle']),
  multipleToast: PropTypes.oneOf(['second', 'third']),
  transitionState: PropTypes.oneOf(['entering', 'entered', 'exiting', 'exited'])
};

VstToast.defaultProps = {
  dismissible: true,
  delay: 5000,
  fadeIn: 400,
  fadeOut: 400,
  maxWidth: 500,
  wordsPerMinute: 120,
  type: 'info',
  imgBtnFn: null,
  closeButtonPosition: 'top',
  transitionState: 'entered'
};
